import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, Send, Loader2, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
// eslint-disable-next-line no-unused-vars
import { AnimatePresence, motion } from 'framer-motion';
import { lessonChat } from '../services/api';
import { trackChatEvent } from '../utils/telemetry';
import ReactMarkdown from 'react-markdown';

/**
 * LessonChatPanel — Phase 3.1: Contextual lesson chat.
 *
 * Renders below the deliverables panel in PostGenerationView.
 * Advisory-only: suggests changes but does not modify lesson state.
 *
 * Props:
 *   result        — lesson generation result (contains data, lesson_id, etc.)
 *   editedData    — current edited lesson data (overrides result.data)
 *   selectedSubject, gradeLevel, lessonDate — lesson metadata
 *   userProfile   — { email, name, ... }
 *   currentVersion — current lesson version number
 */
const LessonChatPanel = ({
    result,
    editedData,
    selectedSubject,
    gradeLevel,
    lessonDate,
    userProfile,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [chatInput, setChatInput] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Auto-scroll when messages change
    useEffect(() => {
        if (isExpanded) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [chatHistory, isLoading, isExpanded]);

    // Track open when first expanded
    useEffect(() => {
        if (isExpanded && chatHistory.length === 0) {
            trackChatEvent('lesson_chat_opened', {
                lesson_id: result?.lesson_id || null,
                has_lesson_data: !!(editedData || result?.data),
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isExpanded]);

    // Build the lesson context payload from available data
    const buildLessonContext = () => {
        const data = editedData || result?.data || {};
        return {
            subject: selectedSubject || null,
            grade_level: gradeLevel || null,
            lesson_date: lessonDate || null,
            unit_name: data.unit_name || null,
            objectives: data.objectives || [],
            lesson_flow: (data.lesson_flow || []).map(step => ({
                activity: step.activity || step.title || null,
                time: step.time || step.duration || null,
                content: step.content || step.student_facing || null,
            })),
            materials: data.materials || [],
            differentiation: data.differentiation || null,
            assessment: data.assessment || null,
        };
    };

    // Resolve a typed error message from the backend error_code field.
    // Returns null if the response was successful.
    const resolveErrorMessage = (responseData, httpErr) => {
        const code = responseData?.error_code;
        if (code === 'AUTH_REQUIRED') {
            return 'Your session has expired. Please refresh and sign in again.';
        }
        if (code === 'QUOTA_EXCEEDED') {
            return "You've reached the chat limit for this session. Start a new lesson to continue.";
        }
        if (code === 'PROVIDER_ERROR') {
            return 'Saige is having trouble right now. Please try again in a moment.';
        }
        if (code === 'FEATURE_DISABLED') {
            return 'This feature is not currently available.';
        }
        if (httpErr) {
            return 'I ran into a problem answering your question. Please try again in a moment.';
        }
        return null;
    };

    const handleSend = async (message) => {
        const text = (message || chatInput).trim();
        if (!text || isLoading) return;

        setChatInput('');

        // Add user message
        setChatHistory(prev => [...prev, { role: 'user', text }]);

        setIsLoading(true);

        trackChatEvent('lesson_chat_prompt_sent', {
            lesson_id: result?.lesson_id || null,
            prompt_length: text.length,
            is_starter: !!message, // true if triggered by starter prompt button
            history_length: chatHistory.length,
            is_multi_turn: chatHistory.length > 0, // value signal: continuing a conversation
        });

        try {
            const response = await lessonChat.send({
                teacher_email: userProfile?.email,
                message: text,
                lesson_id: result?.lesson_id || null,
                lesson_context: buildLessonContext(),
                history: chatHistory.map(msg => ({
                    role: msg.role === 'ai' ? 'ai' : 'user',
                    text: msg.text,
                })),
            });

            // Check for typed error codes in a 2xx response
            const errorCode = response.data?.error_code;
            if (errorCode && errorCode !== null) {
                // Track specific failure telemetry
                if (errorCode === 'AUTH_REQUIRED') {
                    trackChatEvent('lesson_chat_auth_failure', { lesson_id: result?.lesson_id || null });
                } else if (errorCode === 'QUOTA_EXCEEDED') {
                    trackChatEvent('lesson_chat_quota_hit', { lesson_id: result?.lesson_id || null });
                } else if (errorCode === 'PROVIDER_ERROR') {
                    trackChatEvent('lesson_chat_provider_failure', { lesson_id: result?.lesson_id || null });
                } else if (errorCode === 'FEATURE_DISABLED') {
                    trackChatEvent('lesson_chat_feature_disabled', { lesson_id: result?.lesson_id || null });
                }
                const errorMsg = resolveErrorMessage(response.data, false);
                setChatHistory(prev => [...prev, {
                    role: 'ai',
                    text: errorMsg || 'I ran into a problem answering your question. Please try again in a moment.',
                }]);
                return;
            }

            const aiText = response.data?.response || 'I was unable to generate a response. Please try again.';

            setChatHistory(prev => [...prev, { role: 'ai', text: aiText }]);

            trackChatEvent('lesson_chat_response_received', {
                lesson_id: result?.lesson_id || null,
                response_length: aiText.length,
                success: response.data?.success !== false,
                turn_number: chatHistory.filter(m => m.role === 'user').length + 1,
                // Quota proximity signal (ops + UX future use)
                quota_remaining: response.data?.quota_remaining ?? null,
                quota_total: response.data?.quota_total ?? null,
            });

        } catch (err) {
            console.error('Lesson chat error:', err);
            const errorMsg = resolveErrorMessage(err.response?.data, true);
            setChatHistory(prev => [...prev, {
                role: 'ai',
                text: errorMsg || 'I ran into a problem answering your question. Please try again in a moment.',
            }]);
        } finally {
            setIsLoading(false);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    };

    const handleCopy = async (text, id) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 2000);
        } catch { /* silent */ }
    };

    const STARTER_PROMPTS = [
        'Simplify this lesson for struggling readers',
        'Add discussion questions',
        'Create accommodations for IEP students',
        'Rewrite the warm-up for higher engagement',
    ];

    return (
        <div className="mt-4">
            {/* Toggle header */}
            <button
                onClick={() => {
                    setIsExpanded(!isExpanded);
                    if (!isExpanded) {
                        setTimeout(() => inputRef.current?.focus(), 200);
                    }
                }}
                className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200/60 rounded-xl hover:border-violet-300 transition-all group"
                id="lesson-chat-toggle"
            >
                <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center shadow-sm">
                        <Sparkles size={14} className="text-white" />
                    </div>
                    <span className="text-sm font-semibold text-violet-800">Ask Saige about this lesson</span>
                    {chatHistory.length > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-600 font-medium">
                            {chatHistory.filter(m => m.role === 'ai').length} {chatHistory.filter(m => m.role === 'ai').length === 1 ? 'reply' : 'replies'}
                        </span>
                    )}
                </div>
                {isExpanded ? (
                    <ChevronUp size={16} className="text-violet-400 group-hover:text-violet-600 transition-colors" />
                ) : (
                    <ChevronDown size={16} className="text-violet-400 group-hover:text-violet-600 transition-colors" />
                )}
            </button>

            {/* Expandable chat body */}
            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: 'easeInOut' }}
                        className="overflow-hidden"
                    >
                        <div className="mt-2 border border-slate-200/80 rounded-xl bg-white overflow-hidden shadow-sm">

                            {/* Starter prompts — shown only when no messages */}
                            {chatHistory.length === 0 && (
                                <div className="p-3 space-y-1.5">
                                    <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider px-1">
                                        Try asking
                                    </p>
                                    {STARTER_PROMPTS.map((prompt, i) => (
                                        <button
                                            key={i}
                                            onClick={() => handleSend(prompt)}
                                            disabled={isLoading}
                                            className="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-violet-50 hover:text-violet-700 transition-colors disabled:opacity-50"
                                        >
                                            {prompt}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Chat messages */}
                            {chatHistory.length > 0 && (
                                <div className="max-h-80 overflow-y-auto p-3 space-y-3">
                                    {chatHistory.map((msg, idx) => (
                                        <div
                                            key={idx}
                                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                        >
                                            <div className={`max-w-[90%] px-3 py-2.5 rounded-xl text-sm leading-relaxed ${
                                                msg.role === 'user'
                                                    ? 'bg-slate-100 text-slate-700 rounded-br-sm'
                                                    : 'bg-violet-50 text-slate-700 border border-violet-100 rounded-bl-sm'
                                            }`}>
                                                {msg.role === 'ai' ? (
                                                    <div>
                                                        <div className="prose prose-sm prose-slate max-w-none [&>p]:my-2 [&>p:last-child]:mb-0 [&>ul]:my-1 [&>ol]:my-1 prose-li:my-0 prose-headings:text-sm prose-headings:font-bold prose-headings:mb-1">
                                                            <ReactMarkdown>{msg.text}</ReactMarkdown>
                                                        </div>
                                                        <button
                                                            onClick={() => handleCopy(msg.text, idx)}
                                                            className="mt-2 flex items-center gap-1 text-[11px] text-slate-400 hover:text-violet-500 transition-colors"
                                                        >
                                                            {copiedId === idx ? (
                                                                <><Check size={12} className="text-green-500" /><span className="text-green-500">Copied</span></>
                                                            ) : (
                                                                <><Copy size={12} /><span>Copy</span></>
                                                            )}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    msg.text
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    {isLoading && (
                                        <div className="flex justify-start">
                                            <div className="px-3 py-2.5 rounded-xl bg-violet-50 border border-violet-100 rounded-bl-sm flex items-center gap-2">
                                                <Loader2 size={14} className="animate-spin text-violet-500" />
                                                <span className="text-xs text-slate-400">Thinking...</span>
                                            </div>
                                        </div>
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>
                            )}

                            {/* Input */}
                            <div className="border-t border-slate-100 p-2 flex items-center gap-2">
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={chatInput}
                                    onChange={(e) => setChatInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSend();
                                        }
                                    }}
                                    placeholder="Ask about this lesson..."
                                    disabled={isLoading}
                                    className="flex-1 px-3 py-2 text-sm bg-slate-50 rounded-lg border border-slate-200 focus:border-violet-300 focus:ring-1 focus:ring-violet-100 outline-none transition-all placeholder:text-slate-300 disabled:opacity-50"
                                    id="lesson-chat-input"
                                />
                                <button
                                    onClick={() => handleSend()}
                                    disabled={!chatInput.trim() || isLoading}
                                    className="p-2 rounded-lg bg-violet-500 text-white hover:bg-violet-600 disabled:opacity-40 disabled:hover:bg-violet-500 transition-colors"
                                    id="lesson-chat-send"
                                >
                                    <Send size={16} />
                                </button>
                            </div>

                            {/* Beta label */}
                            <div className="px-3 pb-2">
                                <p className="text-[10px] text-slate-300 text-center">
                                    Saige may occasionally provide inaccurate suggestions. Always review before using in class.
                                </p>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default LessonChatPanel;
